import { useCallback, useEffect, useMemo, useState } from "react";

import { SettingsContext } from "@/context/settings-context";
import { ColorThemeModal } from "@/features/modals/color-theme-modal";
import { ContrastModes, type SettingsProviderProps } from "@/types/settings";

const isContrastMode = (value: null | string): value is ContrastModes =>
	value === ContrastModes.DARK ||
	value === ContrastModes.LIGHT ||
	value === ContrastModes.SYSTEM;

/**
 * Theme preference + color-theme modal visibility.
 * Applies `dark-mode` on `<html>` and persists to localStorage (FOUC key).
 */
export const SettingsProvider = ({
	children,
	darkModeClass = "dark-mode",
	defaultContrastMode = ContrastModes.SYSTEM,
	storageKey = "ui-theme",
}: SettingsProviderProps) => {
	const [contrastMode, setContrastMode] = useState<ContrastModes>(() => {
		if (typeof window === "undefined") return defaultContrastMode;
		const saved = localStorage.getItem(storageKey);
		return isContrastMode(saved) ? saved : defaultContrastMode;
	});
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

	useEffect(() => {
		const applyTheme = () => {
			const root = window.document.documentElement;

			if (contrastMode === ContrastModes.SYSTEM) {
				const prefersDark = window.matchMedia(
					"(prefers-color-scheme: dark)",
				).matches;
				root.classList.toggle(darkModeClass, prefersDark);
				localStorage.removeItem(storageKey);
			} else {
				root.classList.toggle(
					darkModeClass,
					contrastMode === ContrastModes.DARK,
				);
				localStorage.setItem(storageKey, contrastMode);
			}
		};

		applyTheme();

		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = () => {
			if (contrastMode === ContrastModes.SYSTEM) {
				applyTheme();
			}
		};

		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [contrastMode, darkModeClass, storageKey]);

	const closeSettingsModal = useCallback(() => {
		setIsSettingsModalOpen(false);
	}, []);

	const value = useMemo(
		() => ({
			contrastMode,
			isSettingsModalOpen,
			setContrastMode,
			setIsSettingsModalOpen,
		}),
		[contrastMode, isSettingsModalOpen],
	);

	return (
		<SettingsContext value={value}>
			{children}
			{isSettingsModalOpen ? (
				<ColorThemeModal isOpen onClose={closeSettingsModal} />
			) : null}
		</SettingsContext>
	);
};
